import React, { useEffect, useMemo, useState } from 'react';
import API from '../../api';
import '../css/DocumentDrafts.css';
import DocumentModal from './DocumentModal';
import ShowDocumnetModal from './ShowDocumnetModal.jsx';

/** Normalize one MPA record from your API to fields we render. */
function mapMPA(record, idx) {
  const id = record.id ?? idx;
  return {
    id,
    lead_id: record.lead_id ?? null,
    name: record.name || 'Untitled',
    mobile: record.phone || '',
    email: record.email || '',
    agent: record.agent || '',
    bname: record.bname || '',
    "DBA-Name": record["DBA-Name"] || '',
    createdby: record.createdby || '',
    created_at: record.created_at ? new Date(record.created_at) : null,
    status: record.status || '',
    updated_at: record.updated_at ? new Date(record.updated_at) : null,
    is_reprogram: record.is_reprogram ?? 0,
    is_duplicate: record.is_duplicate ?? 0,
    source_lead_id: record.source_lead_id ?? null,
  };
}

function formatDateTime(d) {
  if (!d) return null;
  try {
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  } catch {
    return null;
  }
}

function safeJsonParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function normalizeRole(role) {
  return String(role || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-');
}

function normalizeStr(v) {
  return String(v || '').trim().toLowerCase();
}

/** JWT decode (if you store a JWT token). */
function getUserFromToken() {
  const tok = window.localStorage.getItem('token') || window.sessionStorage.getItem('token');
  if (!tok) return {};
  const parts = tok.split('.');
  if (parts.length < 2) return {};
  try {
    const json = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return json?.user || json || {};
  } catch {
    return {};
  }
}

/**
 * Read persisted auth from redux-persist.
 * Your screenshots show key: `persist:root`.
 * (Older builds sometimes used `persistroot`, so we support both.)
 */
function getPersistedAuth() {
  const keysToTry = ['persist:root', 'persistroot'];
  for (const key of keysToTry) {
    const raw = window.localStorage.getItem(key);
    if (!raw) continue;

    const root = safeJsonParse(raw);
    if (!root) continue;

    // many setups store auth slice as a JSON-string inside the root
    const authObj = typeof root?.auth === 'string' ? safeJsonParse(root.auth) : root?.auth;
    if (authObj) return authObj;
  }
  return null;
}

/**
 * IMPORTANT:
 * Prefer redux-persist auth (matches your screenshots),
 * then token,
 * then localStorage user object,
 * then last resort localStorage role.
 */
function getCurrentAuth() {
  // 1) persisted
  const persisted = getPersistedAuth();
  if (persisted) {
    const pUser = persisted?.user || persisted;
    const username = pUser?.username || persisted?.username || '';
    const role = pUser?.role || persisted?.role || '';
    const ass = pUser?.ass ?? persisted?.ass ?? null;
    return { username: String(username || ''), name: '', role: String(role || ''), ass };
  }

  // 2) token
  const tUser = getUserFromToken();
  const tUsername = tUser?.username || tUser?.preferred_username || '';
  const tRole = tUser?.role || '';
  const tAss = tUser?.ass ?? null;
  if (tUsername || tRole) return { username: String(tUsername || ''), name: '', role: String(tRole || ''), ass: tAss };

  // 3) localStorage user object
  const rawUser = window.localStorage.getItem('user');
  if (rawUser) {
    const uObj = safeJsonParse(rawUser);
    if (uObj) {
      const uUsername = uObj?.username || '';
      const uRole = uObj?.role || '';
      const uAss = uObj?.ass ?? null;
      if (uUsername || uRole) return { username: String(uUsername || ''), name: '', role: String(uRole || ''), ass: uAss };
    }
  }

  // 4) last resort localStorage role
  const lsRole = window.localStorage.getItem('role') || '';
  return { username: '', name: '', role: String(lsRole), ass: null };
}

/**
 * Extract list from many possible API shapes:
 * - resp.data is array
 * - resp.data.items
 * - resp.data.data.items
 * - resp.data.data
 * - resp.data.results
 */
function extractList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.rows)) return payload.rows;

  if (payload.data && typeof payload.data === 'object') {
    if (Array.isArray(payload.data.items)) return payload.data.items;
    if (Array.isArray(payload.data.data)) return payload.data.data;
    if (payload.data.data && typeof payload.data.data === 'object' && Array.isArray(payload.data.data.items)) {
      return payload.data.data.items;
    }
    if (Array.isArray(payload.data.results)) return payload.data.results;
  }

  // fallback: search one level deep for any array
  for (const v of Object.values(payload)) {
    if (Array.isArray(v)) return v;
    if (v && typeof v === 'object') {
      for (const vv of Object.values(v)) {
        if (Array.isArray(vv)) return vv;
      }
    }
  }
  return [];
}

/**
 * Visibility rules (client-side):
 * - admin: see all
 * - sub-admin: createdby === username
 * - agent: agent === username
 *
 * Matching is case-insensitive + trimmed.
 */
function applyRoleFilter(list, role, username) {
  const roleNorm = normalizeRole(role);
  const arr = Array.isArray(list) ? list : [];

  if (roleNorm === 'admin' || roleNorm === 'super-admin') return arr;

  const uname = normalizeStr(username);
  if (!uname) return [];

  if (roleNorm === 'sub-admin' || roleNorm === 'subadmin') {
    // Server already filters by createdby OR lead ass match — trust server result
    return arr;
  }

  if (roleNorm === 'agent') {
    return arr.filter((r) => normalizeStr(r?.agent) === uname);
  }

  return arr;
}

/** Normalize role for API */
function roleForApi(role) {
  const r = normalizeRole(role);
  if (r === 'subadmin') return 'sub-admin';
  return r;
}

/**
 * Build payload for /auth/mpa-list.
 * KEY FIX: for agent role, send `agent: username` in addition to username.
 * (Your backend appears to return {items:[]} otherwise.)
 */
function buildMPAListPayload(role, username, ass) {
  const roleNorm = roleForApi(role);

  // backend returns empty for role=agent, so fetch as admin and filter locally
  const fetchRole = roleNorm === 'agent' ? 'admin' : roleNorm;

  const payload = { role: fetchRole, username };

  if (roleNorm === 'agent') payload.agent = username;
  if (roleNorm === 'sub-admin') {
    payload.createdby = username;
    if (ass !== null && ass !== undefined) payload.ass = ass;
  }

  return payload;
}

/** status -> class */
function statusClass(status) {
  const raw = String(status || '').trim();
  const s = raw.toLowerCase().replace(/\s+/g, '');

  if (!s) return 'cb-status-neutral';
  if (raw === 'HF-Filled') return 'cb-status-draft';
  if (s.includes('declined') || s.includes('notinterested')) return 'cb-status-notintrested';
  if (s.includes('sent') || s.includes('submitted')) return 'cb-status-callback';
  if (s.includes('signed')) return 'cb-status-sold';
  if (s.includes('approved')) return 'cb-status-answering';
  if (s.includes('pendingapproval')) return 'cb-status-neutral';

  return 'cb-status-neutral';
}

const STATUS_OPTIONS = ['sent', 'signed', 'approved', 'HF-Filled', 'submitted', 'Pending Approval', 'Declined'];

export default function Drafts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [currentRole, setCurrentRole] = useState('');
  const [currentUsername, setCurrentUsername] = useState('');

  // Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLead, setModalLead] = useState(null);

  // View modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLead, setViewLead] = useState(null);

  // Filters
  const [agentFilter, setAgentFilter] = useState('All');
  const [createdByFilter, setCreatedByFilter] = useState('All');

  // ✅ Status multi-filter (checkboxes). Default: all selected.
  const [statusSelected, setStatusSelected] = useState(() => new Set());

  // ✅ Search (realtime)
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [agentFilter, createdByFilter, statusSelected, searchTerm, rows]);

  const canEdit = useMemo(() => normalizeRole(currentRole) !== 'agent', [currentRole]);

  // Load list
  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setErr('');

        const auth = getCurrentAuth();
        const role = roleForApi(auth.role);
        const username = auth.username;
        const ass = auth.ass;

        if (!ignore) {
          setCurrentRole(role);
          setCurrentUsername(username);
        }

        const payload = buildMPAListPayload(role, username, ass);

        const resp = await API.post('/auth/mpa-list', payload);

        const list = extractList(resp?.data);

        // client filter (even if server does it, keep this for safety)
        const visibleList = applyRoleFilter(list, role, username);

        if (!ignore) setRows(visibleList.map(mapMPA));
      } catch (e) {
        console.error(e);
        if (!ignore) setErr('Could not load items.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, []);

  const allSelected = useMemo(
    () => rows.length > 0 && rows.every((r) => selectedIds.has(r.id)),
    [rows, selectedIds]
  );

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (rows.length === 0) return prev;
      const every = rows.every((r) => prev.has(r.id));
      if (every) return new Set();
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  const agentOptions = useMemo(() => Array.from(new Set(rows.map((r) => r.agent).filter(Boolean))).sort(), [rows]);

  const createdByOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.createdby).filter(Boolean))).sort(),
    [rows]
  );

  const statusOptions = useMemo(() => {
    const apiStatuses = Array.from(new Set(rows.map((r) => r.status).filter(Boolean)));
    return Array.from(new Set([...STATUS_OPTIONS, ...apiStatuses]));
  }, [rows]);

  // ✅ default: all statuses selected (re-applied when list changes / reloads)
  useEffect(() => {
    setStatusSelected(new Set(statusOptions));
  }, [statusOptions]);

  const allStatusesSelected = useMemo(
    () => statusOptions.length > 0 && statusOptions.every((s) => statusSelected.has(s)),
    [statusOptions, statusSelected]
  );

  const toggleStatus = (s) => {
    setStatusSelected((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const toggleAllStatuses = () => {
    setStatusSelected((prev) => {
      const every = statusOptions.length > 0 && statusOptions.every((s) => prev.has(s));
      return every ? new Set() : new Set(statusOptions);
    });
  };

  // ✅ Search matcher (letter-by-letter)
  const matchesSearch = (row, term) => {
    const q = normalizeStr(term);
    if (!q) return true;

    const haystack = [
      row?.name,
      row?.mobile,
      row?.email,
      row?.agent,
      row?.bname,
      row?.createdby,
      row?.status,
      row?.lead_id,
    ]
      .filter((v) => v !== null && v !== undefined)
      .map((v) => normalizeStr(v))
      .join(' | ');

    return haystack.includes(q);
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (agentFilter !== 'All' && r.agent !== agentFilter) return false;
      if (createdByFilter !== 'All' && r.createdby !== createdByFilter) return false;

      // status checkbox filter
      const st = String(r.status || '');
      if (statusSelected.size > 0 && !statusSelected.has(st)) return false;

      if (!matchesSearch(r, searchTerm)) return false;
      return true;
    });
  }, [rows, agentFilter, createdByFilter, statusSelected, searchTerm]);

  const openEditModal = (row) => {
    // If agent, block edit modal entirely
    if (!canEdit) return;

    setModalLead({
      lead_id: row.lead_id,
      name: row.name,
      bname: row.bname,
      email: row.email,
      mobile: row.mobile,
      agent: row.agent,
      is_reprogram: row.is_reprogram,
      is_duplicate: row.is_duplicate,
      source_lead_id: row.source_lead_id,
    });
    setModalOpen(true);
  };

  const openViewModal = (row) => {
    setViewLead({
      lead_id: row.lead_id,
      name: row.name,
      bname: row.bname,
      email: row.email,
      mobile: row.mobile,
      agent: row.agent,
      is_reprogram: row.is_reprogram,
      is_duplicate: row.is_duplicate,
      source_lead_id: row.source_lead_id,
    });
    setViewOpen(true);
  };

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
     if (pageSize === -1) return filteredRows;
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const reloadMPAList = async () => {
    try {
      setLoading(true);
      setErr('');

      const auth = getCurrentAuth();
      const role = roleForApi(auth.role);
      const username = auth.username;
      const ass = auth.ass;

      setCurrentRole(role);
      setCurrentUsername(username);

      const payload = buildMPAListPayload(role, username, ass);
      const resp = await API.post('/auth/mpa-list', payload);

      const list = extractList(resp?.data);
      const visibleList = applyRoleFilter(list, role, username);
      setRows(visibleList.map(mapMPA));
    } catch (e) {
      console.error(e);
      setErr('Could not load items.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  return (
    <div className="cb-wrap">
      <div className="cb-header">
        <div className="cb-header-left">
          <label className="cb-chip ht-chip">
            <input type="radio" name="status" defaultChecked />
            <span>Drafts</span>
          </label>

          <div className="cb-filters">
            <div className="cb-filter-group">
              <select className="cb-filter-select" value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}>
                <option value="All">Agent: All</option>
                {agentOptions.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
            <div className="cb-filter-group">
              <select
                className="cb-filter-select"
                value={createdByFilter}
                onChange={(e) => setCreatedByFilter(e.target.value)}
              >
                <option value="All">Created By: All</option>
                {createdByOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            

            {/* ✅ Realtime Search Box */}
            <div className="cb-filter-group">
              <input
                type="text"
                className="cb-filter-select"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search drafts"
              />
            </div>

            {/* ✅ Optional: Clear button (small UX improvement) */}
            {searchTerm ? (
              <button
                type="button"
                className="cb-page-btn"
                onClick={() => setSearchTerm('')}
                style={{ marginLeft: 8 }}
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>

        <div className="cb-pagination-bar">
          <div className="cb-page-size">
            <label>
              Show
              <select
                className="cb-filter-chip"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                 <option value={-1}>All</option>
              </select>
              rows
            </label>
            <span className="cb-page-meta">&nbsp;•&nbsp; {filteredRows.length} total</span>
          </div>

          <div className="cb-pager">
            <button
              type="button"
              className="cb-page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              ‹ Prev
            </button>

            <span className="cb-page-status">
              Page {page} of {pageCount}
            </span>

            <button
              type="button"
              className="cb-page-btn"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page >= pageCount}
              aria-label="Next page"
            >
              Next ›
            </button>
          </div>
        </div>
      </div>
       <div className="cb-header">
        <div className="cb-filter-group cb-status-filter">
              <div className="cb-status-filter-title">Status:</div>

              <label className="cb-status-item cb-status-all">
                <input
                  type="checkbox"
                  checked={allStatusesSelected}
                  onChange={toggleAllStatuses}
                  aria-label="Toggle all statuses"
                />
                <span>All</span>
              </label>

              <div className="cb-status-box" role="group" aria-label="Status filters">
                {statusOptions.map((s) => (
                  <label key={s} className="cb-status-item">
                    <input
                      type="checkbox"
                      checked={statusSelected.has(s)}
                      onChange={() => toggleStatus(s)}
                      aria-label={`Toggle status ${s}`}
                    />
                    <span>{s}</span>
                  </label>
                ))}
              </div>
            </div>
       </div>

      <div className="cb-table document-table">
        <div className="cb-row cb-row-head">
          <div className="cb-col cb-col-name">
            <input
              className="cb-bullet"
              type="checkbox"
              aria-label="Select all"
              checked={allSelected}
              onChange={toggleAll}
            />
            Name
          </div>

          <div className="cb-col cb-col-mobile">Phone</div>
          <div className="cb-col cb-col-agent">Agent</div>
          <div className="cb-col cb-col-biz">DBA</div>
          <div className="cb-col cb-col-createdby">Created By</div>
          <div className="cb-col cb-col-createdat">Created At</div>
          <div className="cb-col cb-col-status">Status</div>
          <div className="cb-col cb-col-updatedat">Updated At</div>
          <div className="cb-col cb-col-menu" />
        </div>

        {loading && (
          <div className="cb-row">
            <div className="cb-col cb-col-full">Loading…</div>
          </div>
        )}

        {err && (
          <div className="cb-row">
            <div className="cb-col cb-col-full cb-error">{err}</div>
          </div>
        )}

        {!loading && !err && filteredRows.length === 0 && (
          <div className="cb-row">
            <div className="cb-col cb-col-full">No items.</div>
          </div>
        )}

        {!loading &&
          !err &&
          paginatedRows.map((r) => {
            const isChecked = selectedIds.has(r.id);

            const onRowClick = () => {
              // ❌ Agent: do nothing
              if (!canEdit) return;

              // Admin / Sub-admin
              openEditModal(r);
            };

            return (
              <div className="cb-row" key={r.id} onClick={onRowClick}>
                <div className="cb-col cb-col-name">
                  <input
                    className="cb-bullet"
                    type="checkbox"
                    aria-label={`Select ${r.name}`}
                    checked={isChecked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleOne(r.id)}
                  />

                  <button
                    className="cb-linklike"
                    onClick={(e) => {
                      e.stopPropagation();

                      // ❌ Agent: no modal at all
                      if (!canEdit) return;

                      openEditModal(r);
                    }}
                    title={canEdit ? 'Edit document' : 'No access'}
                    type="button"
                  >
                    {r.name}
                  </button>
                </div>

                <div className="cb-col cb-col-mobile">{r.mobile || <span className="cb-muted">—</span>}</div>
                <div className="cb-col cb-col-agent">{r.agent || <span className="cb-muted">—</span>}</div>
                <div className="cb-col cb-col-biz">{r["DBA-Name"] || <span className="cb-muted">—</span>}</div>
                <div className="cb-col cb-col-createdby">{r.createdby || <span className="cb-muted">—</span>}</div>
                <div className="cb-col cb-col-createdat">
                  {formatDateTime(r.created_at) || <span className="cb-muted">—</span>}
                </div>
                <div className="cb-col cb-col-status">
                  {r.status ? (
                    <span className={`cb-status ${statusClass(r.status)}`}>{r.status}</span>
                  ) : (
                    <span className="cb-muted">—</span>
                  )}
                </div>
                <div className="cb-col cb-col-updatedat">
                  {formatDateTime(r.updated_at) || <span className="cb-muted">—</span>}
                </div>

                <div className="cb-col cb-col-menu">
                  <button
                    className="cb-kebab"
                    aria-label="View document"
                    onClick={(e) => {
                      e.stopPropagation();

                      // ❌ Agent: block
                      if (!canEdit) return;

                      openViewModal(r);
                    }}
                    type="button"
                  >
                    View
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* EDIT MODAL: NOT RENDERED FOR AGENT */}
      {canEdit && (
        <DocumentModal
          key={modalLead?.lead_id || 'doc-modal'}
          open={modalOpen}
          lead={modalLead}
          onClose={() => {
            setModalOpen(false);
            setModalLead(null);
          }}
          onSaved={async () => {
            await reloadMPAList();
            setModalOpen(false);
            setModalLead(null);
          }}
        />
      )}

      {/* VIEW MODAL */}
      {canEdit && (
        <ShowDocumnetModal
          open={viewOpen}
          lead={viewLead}
          onClose={() => {
            setViewOpen(false);
            setViewLead(null);
          }}
          onSaved={async () => {
            await reloadMPAList();
            setViewOpen(false);
            setViewLead(null);
          }}
        />
      )}
    </div>
  );
}